import { Filter, X } from 'lucide-react';
import { useState } from 'react';
import type { Lookups } from '../api/schemas';
import { GradeLegend } from './GradeLegend';

export interface FilterValues {
  category_id: number | undefined;
  city_id: number | undefined;
  grade: string | undefined;
  zip: string | undefined;
  hotel: string | undefined;
  address: string | undefined;
  search_type: 'contains' | 'starts_with';
}

export const EMPTY_FILTERS: FilterValues = {
  category_id: undefined,
  city_id: undefined,
  grade: undefined,
  zip: undefined,
  hotel: undefined,
  address: undefined,
  search_type: 'contains',
};

interface Props {
  values: FilterValues;
  lookups: Lookups | undefined;
  onChange: (values: FilterValues) => void;
  onClear: () => void;
  leftAction?: React.ReactNode | undefined;
}

function isActive(v: FilterValues): boolean {
  return (
    v.category_id !== undefined ||
    v.city_id !== undefined ||
    !!v.grade ||
    !!v.zip ||
    !!v.hotel ||
    !!v.address ||
    v.search_type !== 'contains'
  );
}

export function FiltersPanel({ values, lookups, onChange, onClear, leftAction }: Props) {
  const [open, setOpen] = useState(false);
  const active = isActive(values);

  const update = <K extends keyof FilterValues>(key: K, v: FilterValues[K]) =>
    onChange({ ...values, [key]: v });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {leftAction}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            className="flex items-center space-x-2 px-4 py-2 bg-snhd-primary text-white rounded-lg hover:bg-snhd-primary-hover transition-colors"
          >
            <Filter className="w-4 h-4" aria-hidden />
            <span className="font-medium">Advanced Filters</span>
            {active && (
              <span className="ml-2 px-2 py-0.5 bg-snhd-primary-hover text-white text-xs rounded-full">
                Active
              </span>
            )}
          </button>
        </div>
        {active && (
          <button
            type="button"
            onClick={onClear}
            className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <X className="w-4 h-4" aria-hidden />
            <span>Clear filters</span>
          </button>
        )}
      </div>

      {open && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <Field label="Establishment Type">
              <select
                value={values.category_id ?? ''}
                onChange={(e) =>
                  update('category_id', e.target.value ? Number(e.target.value) : undefined)
                }
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="">All Types</option>
                {lookups?.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Inspection Grade">
              <select
                value={values.grade ?? ''}
                onChange={(e) => update('grade', e.target.value || undefined)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="">All Grades</option>
                {lookups?.grades.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.label === g.code ? `Grade ${g.code}` : `${g.code} (${g.label})`}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="City">
              <select
                value={values.city_id ?? ''}
                onChange={(e) =>
                  update('city_id', e.target.value ? Number(e.target.value) : undefined)
                }
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="">All Cities</option>
                {lookups?.cities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="ZIP Code">
              <input
                type="text"
                inputMode="numeric"
                placeholder="Enter ZIP"
                value={values.zip ?? ''}
                onChange={(e) => update('zip', e.target.value || undefined)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
              />
            </Field>

            <Field label="Hotel / Location">
              <input
                type="text"
                placeholder="e.g. Wynn"
                value={values.hotel ?? ''}
                onChange={(e) => update('hotel', e.target.value || undefined)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
              />
            </Field>

            <Field label="Address">
              <input
                type="text"
                placeholder="e.g. Las Vegas Blvd"
                value={values.address ?? ''}
                onChange={(e) => update('address', e.target.value || undefined)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400"
              />
            </Field>

            <Field label="Match Type">
              <select
                value={values.search_type}
                onChange={(e) =>
                  update('search_type', e.target.value as 'contains' | 'starts_with')
                }
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="contains">Contains</option>
                <option value="starts_with">Starts With</option>
              </select>
            </Field>
          </div>
          <GradeLegend />
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {children}
    </div>
  );
}
